import type { PortalThunk } from '../../index'
import { portalAction, profilingIdSelector, tokenSelector, checkoutModeSelector, paymentOptionsSelector } from '../../index'
import { Checkout, CashAppObject } from '@afterpaytouch/portal-api/types'
import {
  CrossRegionUserDetails,
  CrossRegionRedirection,
  UpdatedCheckout,
  UpdateCheckoutRequest,
  UpdateCheckoutData,
  applyInitialData,
  PaymentOption,
  UpdatedPaymentOption,
  OrderTransactionDocumentType,
  CreateDocumentsRequest,
  RetrieveCardsResponse,
  RetrieveCardsRequest,
  VerifyEFARobocallEventRequest,
} from '../../../model'
import { ErrorCode, IdentityNationalIDNumberRequest, IdentityNationalIDNumberResponse, CheckoutMode } from '@afterpaytouch/portal-api/types'
import { checkoutInitialDataSelector, isPickupSelector, startedCheckoutSelector } from '../hooks'
import { authTokenSelector, cashCheckoutTokenSelector } from '../../auth/user/hooks'
import { fetchVerification } from '@afterpaytouch/event-verify'
import { api } from '../../../env'
// deep import (not the userJourney barrel) to avoid an import cycle back into src/state;
// see src/state/request.ts for the same pattern
import { reportJourneyNetworkFailure } from '../../../integrations/userJourney/networkSignals'

export const getCheckout: PortalThunk<void, Checkout> = portalAction('checkout/get', async (_, thunk) => {
  const cashCheckoutToken = cashCheckoutTokenSelector(thunk.getState())
  const headers = {
    ...(cashCheckoutToken != null && { 'x-cash-token': cashCheckoutToken }),
  }
  const token = tokenSelector(thunk.getState())
  const suffix = api.PORTAL_API_GET_CHECKOUT_PATH_SUFFIX ?? ''
  return await thunk.extra.portalApi().get<Checkout>(`/portal/consumers/checkout${suffix}/${token}`, headers)
})

export const addCashCustomerDetails: PortalThunk<void, CashAppObject | undefined> = portalAction('checkout/cash', async (_, thunk) => {
  const cashCheckoutToken = cashCheckoutTokenSelector(thunk.getState())
  const headers = {
    ...(cashCheckoutToken !== null && { 'x-cash-token': cashCheckoutToken }),
  }
  const token = tokenSelector(thunk.getState())
  return await thunk.extra.portalApi().post(`/portal/consumers/checkout/${token}/cash`, null, headers)
})

export const updateCheckout: PortalThunk<UpdateCheckoutData, UpdatedCheckout> = portalAction('checkout/update', async (request, thunk) => {
  const token = tokenSelector(thunk.getState())
  const profilingSessionId = profilingIdSelector(thunk.getState())
  const isPickUp = isPickupSelector(thunk.getState())
  const payload: UpdateCheckoutRequest = {
    profilingSessionId,
    isPickUp,
    ...request,
  }
  return await thunk.extra.portalApi().put(`/portal/consumers/checkout/${token}/update`, payload)
})

export const crossRegionUserSignIn: PortalThunk<CrossRegionUserDetails, CrossRegionRedirection> = portalAction(
  'checkout/crossRegionUserSignIn',
  async (request, thunk) => {
    const initialData = checkoutInitialDataSelector(thunk.getState())
    const checkoutMode = checkoutModeSelector(thunk.getState())
    const response: CrossRegionRedirection = await thunk.extra
      .portalApi()
      .post(`/portal/consumers/checkout/${initialData.token}/crossRegionUserSignIn`, request)
    const crossRegionCheckoutUrl = response.crossRegionCheckoutUrl

    const transformedInitialData =
      checkoutMode === CheckoutMode.STANDARD
        ? {
            ...initialData,
            shippingOptionRequired: false, // INC-1037 Make sure old checkout does not expect a shipping option for standard checkout
          }
        : initialData

    return { crossRegionCheckoutUrl: applyInitialData(crossRegionCheckoutUrl, transformedInitialData) }
  }
)

export const getPaymentOptions: PortalThunk<void, PaymentOption[]> = portalAction('checkout/paymentOptions', async (_, thunk) => {
  const state = thunk.getState()
  // A one-offer /start carries the plans it priced (CASHDAP-4150) and this GET would return that
  // same persisted list, so the first load takes them as they are. A list loaded since (an
  // update's re-price) can be fresher than /start, so a re-fetch asks the GET as before.
  const carried = startedCheckoutSelector(state)?.paymentOptions
  if (carried != null && carried.length > 0 && paymentOptionsSelector(state).length === 0) {
    return carried
  }
  const token = tokenSelector(state)
  let response: PaymentOption[]
  try {
    response = await thunk.extra.portalApi().get(`/portal/consumers/checkout/${token}/payment-options`)
  } catch (error) {
    console.log(error)
    if (error?.response?.status === 429) {
      error.response.data = { errorCode: ErrorCode.TooManyRequests }
    }
    throw error
  }
  return response
})

export const updatePaymentOptions: PortalThunk<void, PaymentOption[]> = portalAction('checkout/updatePaymentOptions', async (_, thunk) => {
  const token = tokenSelector(thunk.getState())
  return await thunk.extra.portalApi().put(`/portal/consumers/checkout/${token}/payment-options`, null)
})

export const updatePaymentOption: PortalThunk<number | undefined, UpdatedPaymentOption> = portalAction(
  'checkout/paymentOptions/paymentOptionId',
  async (paymentOptionId, thunk) => {
    const token = tokenSelector(thunk.getState())
    return await thunk.extra.portalApi().put(`/portal/consumers/checkout/${token}/payment-options/${paymentOptionId}`, null)
  }
)

export const createDocuments: PortalThunk<CreateDocumentsRequest, Record<OrderTransactionDocumentType, string>> = portalAction(
  'checkout/documents',
  async (request, thunk) => {
    const token = tokenSelector(thunk.getState())
    return await thunk.extra.portalApi().post(`/portal/consumers/checkout/${token}/documents`, request)
  }
)

export const retrieveCards: PortalThunk<void, RetrieveCardsResponse> = portalAction('checkout/retrieveCards', async (request, thunk) => {
  const token = tokenSelector(thunk.getState())
  const profilingSessionId = profilingIdSelector(thunk.getState())
  const payload: RetrieveCardsRequest = {
    profilingSessionId,
  }
  return await thunk.extra.portalApi().post(`/portal/consumers/checkout/${token}/cards`, payload)
})

export const identityNationalIDNumber: PortalThunk<IdentityNationalIDNumberRequest, IdentityNationalIDNumberResponse> = portalAction(
  'checkout/verifyNationalIdNumber',
  async (request, thunk) => {
    const token = tokenSelector(thunk.getState())
    return await thunk.extra.portalApi().post(`/portal/consumers/checkout/${token}/identities/national-id-number`, request)
  },
  true
)

export const verifyEFARobocallEvent: PortalThunk<VerifyEFARobocallEventRequest, void> = portalAction(
  'checkout/efa/robo-call',
  async (verifyEFARobocallEventRequest, thunk) => {
    const token = tokenSelector(thunk.getState())
    const { onSuccess, onError } = verifyEFARobocallEventRequest
    const path = `/portal/consumers/checkout/${token}/efa/robo-call`
    return await fetchVerification({
      url: encodeURI(`${api.PORTAL_API_BASE_URL}${path}`),
      onSuccess: () => {
        onSuccess()
      },
      onError: (d) => {
        onError(d)
      },
      onWaiting: () => {},
      // The SSE stream bypasses portalAxios, so the journey interceptor never sees a
      // failed connection here (CASHDAP-3606). The server-sent ERROR event (the fraud
      // decline handled by onError above) is a business response and stays unsignalled,
      // matching the structured-4xx exemption in reportJourneyNetworkError.
      onTransportError: ({ status }) => {
        reportJourneyNetworkFailure(path, status)
      },
      headers: {
        'x-auth-token': authTokenSelector(thunk.getState()),
      },
    })
  }
)
